import React, { useCallback, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import { makeStyles } from "tss-react/mui";
import * as Icons from "react-feather";
import { Column, Sort } from "component/shared/table";
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS, usePageSizePrefs } from "./use-page-size-prefs";

interface Props<I> {
	items: I[];
	columns: Column<I>[];
	sort: Sort;
	rowKey: (item: I) => React.Key;
	// Only needed when paginating — that is what the rows-per-page preference is scoped to.
	userId?: number;
	tableKey?: string;
	// For fixed-length tables, where every row is always shown and there is nothing to page
	// through: the footer and its rows-per-page control are omitted.
	paginate?: boolean;
	// For a table drawn inside a card that already has the border, e.g. an accordion's body: no
	// border, corners or bottom margin of its own, and the edge cells take the card's 24px inset.
	embedded?: boolean;
}

const useStyles = makeStyles()((theme) => ({
	// Breathing room below the pagination row so the table doesn't butt up against the
	// bottom of the page.
	root: {
		marginBottom: 40,
	},
	tableContainer: {
		backgroundColor: theme.palette.background.paper, // white table card
		border: `1px solid ${theme.palette.custom.borderCard}`, // #DDE7E8
		borderRadius: 8,
		overflowX: "auto",
	},
	headerRow: {
		backgroundColor: theme.palette.custom.tableHeaderSurface, // #F8F8F8 per Figma header cell
	},
	headerCell: {
		padding: "16px",
		backgroundColor: theme.palette.custom.tableHeaderSurface, // #F8F8F8
		fontFamily: theme.typography.fontFamily,
		fontSize: 16,
		fontWeight: 400,
		lineHeight: "24px",
		color: theme.palette.custom.textSecondary, // #5f6368
		borderBottom: `1px solid ${theme.palette.custom.borderCard}`,
		whiteSpace: "nowrap",
		verticalAlign: "middle",
	},
	bodyRow: {
		borderBottom: `1px solid ${theme.palette.custom.borderCard}`,
		"&:last-child": {
			borderBottom: "none",
		},
		"&:hover .showOnHover": {
			visibility: "visible",
		},
	},
	bodyCell: {
		padding: "16px",
		fontFamily: theme.typography.fontFamily,
		fontSize: 16,
		lineHeight: "24px",
		color: theme.palette.custom.textPrimary,
		borderBottom: "none",
		verticalAlign: "middle",
	},
	hoverCell: {
		visibility: "hidden",
	},
	embeddedRoot: {
		marginBottom: 0,
	},
	embeddedContainer: {
		border: "none",
		borderRadius: 0,
	},
	embeddedTable: {
		"& .MuiTableCell-root:first-of-type": {
			paddingLeft: theme.spacing(3),
		},
		"& .MuiTableCell-root:last-of-type": {
			paddingRight: theme.spacing(3),
		},
	},
	pagination: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		padding: "12px 0",
	},
	rowsControl: {
		display: "flex",
		alignItems: "center",
		gap: 8,
	},
	rowsLabel: {
		fontWeight: 700,
		color: theme.palette.primary.main, // teal per Figma (not brandOrange)
	},
	// Keeps the Figma label styling while making it an actual control: no MUI Button padding /
	// uppercasing / min-width, so the text sits exactly where the static Typography did.
	rowsButton: {
		padding: 0,
		minWidth: 0,
		textTransform: "none",
		font: "inherit",
		"&:hover": {
			backgroundColor: "transparent",
			textDecoration: "underline",
		},
	},
	rangeLabel: {
		color: theme.palette.custom.textSecondary,
		marginLeft: 16,
	},
	pageArrows: {
		display: "flex",
		alignItems: "center",
		gap: 8,
	},
	arrowButton: {
		border: `1px solid ${theme.palette.custom.border}`,
		width: 32,
		height: 32,
	},
}));

export function RefreshTable<I>({ items, columns, sort, rowKey, userId, tableKey, paginate = true, embedded }: Props<I>) {
	const { classes, cx } = useStyles();
	const [page, setPage] = useState(0);
	const { readPageSizePref, writePageSizePref } = usePageSizePrefs(userId ?? 0, tableKey ?? "");
	// Seed from localStorage on first render so the persisted size applies before the first
	// paint — hydrating in an effect would flash 10 rows and then re-slice.
	const [pageSize, setPageSize] = useState(() => readPageSizePref() ?? DEFAULT_PAGE_SIZE);
	const [rowsMenuAnchor, setRowsMenuAnchor] = useState<HTMLElement | null>(null);

	const handlePageSizeChange = useCallback((nextPageSize: number) => {
		setPageSize(nextPageSize);
		writePageSizePref(nextPageSize);
		// Jump back to the first page: the row the user was looking at lands on a different
		// page under the new size, so preserving the page index would be arbitrary.
		setPage(0);
		setRowsMenuAnchor(null);
	}, [writePageSizePref]);

	const visibleColumns = useMemo(() => columns.filter((c) => !c.hidden), [columns]);

	const sortedItems = useMemo(() => {
		const next = [...items];
		const sortColumn = visibleColumns.find((c) => c.id === sort.id && c.sort);
		if (sortColumn && sortColumn.sort) {
			next.sort((a, b) => sortColumn.sort!(a, b) * (sort.desc ? -1 : 1));
		}
		return next;
	}, [items, visibleColumns, sort]);

	const pageCount = Math.max(1, Math.ceil(sortedItems.length / pageSize));
	const safePage = Math.min(page, pageCount - 1);
	const start = safePage * pageSize;
	const pageItems = paginate ? sortedItems.slice(start, start + pageSize) : sortedItems;
	const rangeStart = sortedItems.length === 0 ? 0 : start + 1;
	const rangeEnd = Math.min(start + pageSize, sortedItems.length);

	return (
		<div className={cx(classes.root, embedded && classes.embeddedRoot)}>
			<Mui.TableContainer className={cx(classes.tableContainer, embedded && classes.embeddedContainer)}>
				<Mui.Table className={embedded ? classes.embeddedTable : undefined}>
					<Mui.TableHead>
						<Mui.TableRow className={classes.headerRow}>
							{visibleColumns.map((column, index) => (
								<Mui.TableCell
									key={column.id ?? index}
									className={classes.headerCell}
									align={column.align}
									style={{ width: column.width }}
								>
									{column.title}
								</Mui.TableCell>
							))}
						</Mui.TableRow>
					</Mui.TableHead>
					<Mui.TableBody>
						{pageItems.map((item, row) => (
							<Mui.TableRow key={rowKey(item)} className={classes.bodyRow}>
								{visibleColumns.map((column, index) => (
									<Mui.TableCell
										key={column.id ?? index}
										className={classes.bodyCell}
										align={column.align}
									>
										{column.showOnHover ? (
											<div className={`showOnHover ${classes.hoverCell}`}>
												{column.value(item, index, row)}
											</div>
										) : (
											column.value(item, index, row)
										)}
									</Mui.TableCell>
								))}
							</Mui.TableRow>
						))}
					</Mui.TableBody>
				</Mui.Table>
			</Mui.TableContainer>
			{paginate && (
			<div className={classes.pagination}>
				<div className={classes.rowsControl}>
					<Mui.Button
						className={`${classes.rowsLabel} ${classes.rowsButton}`}
						disableRipple
						endIcon={<Icons.ChevronDown size={16} />}
						aria-haspopup="listbox"
						aria-label={`Rows per page: ${pageSize}`}
						onClick={(event) => setRowsMenuAnchor(event.currentTarget)}
					>
						{pageSize} Rows
					</Mui.Button>
					<Mui.Menu
						anchorEl={rowsMenuAnchor}
						open={!!rowsMenuAnchor}
						onClose={() => setRowsMenuAnchor(null)}
						anchorOrigin={{ vertical: "top", horizontal: "left" }}
						transformOrigin={{ vertical: "bottom", horizontal: "left" }}
					>
						{PAGE_SIZE_OPTIONS.map((option) => (
							<Mui.MenuItem
								key={option}
								selected={option === pageSize}
								onClick={() => handlePageSizeChange(option)}
							>
								{option} Rows
							</Mui.MenuItem>
						))}
					</Mui.Menu>
					<Mui.Typography className={classes.rangeLabel}>
						{rangeStart}-{rangeEnd} of {sortedItems.length}
					</Mui.Typography>
				</div>
				<div className={classes.pageArrows}>
					<Mui.IconButton
						className={classes.arrowButton}
						size="small"
						disabled={safePage === 0}
						onClick={() => setPage((p) => Math.max(0, p - 1))}
					>
						<Icons.ChevronLeft size={18} />
					</Mui.IconButton>
					<Mui.IconButton
						className={classes.arrowButton}
						size="small"
						disabled={safePage >= pageCount - 1}
						onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
					>
						<Icons.ChevronRight size={18} />
					</Mui.IconButton>
				</div>
			</div>
			)}
		</div>
	);
}
